import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { JhiItemCount, JhiPagination, Translate, getSortState, translate } from 'react-jhipster';
import { Link, RouteComponentProps } from 'react-router-dom';
import { Button, Row, Table } from 'reactstrap';

import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { MaintenanceModelModal } from 'app/modules/maintenance-model/MaintenanceModelModal';
import { hasAnyAuthority, isUserAuthorizedForMaintenanceByCompany } from 'app/shared/auth/private-route';
import { PageFilters } from 'app/shared/components/page-filters/PageFilters';
import { PageHeader } from 'app/shared/components/page-header/PageHeader';
import { ISearchTypes } from 'app/shared/interfaces/searchTypesInterface';
import { IMaintenanceModel } from 'app/shared/model/maintenance-model.model';
import { showElement } from 'app/shared/util/data-utils';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { Loading } from 'app/shared/util/loading';
import { ASC, DEFAULT_ITEMS_PER_PAGE, DESC } from 'app/shared/util/pagination.constants';
import { isAdminByCompanyType } from 'app/shared/util/role-utils';
import { getMaintenanceModelSummary } from './maintenance-model.reducer';

export const MaintenanceModel = (props: RouteComponentProps<{ url: string }>) => {
  const dispatch = useAppDispatch();

  const totalItems = useAppSelector(state => state.maintenanceModel.totalItems);
  const maintenanceModelInfoList = useAppSelector(state => state.maintenanceModel.entities as IMaintenanceModel[]);
  const loading = useAppSelector(state => state.maintenanceModel.loading);
  const updateSuccess = useAppSelector(state => state.maintenanceModel.updateSuccess);
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const isManufacturerAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MANUFACTURER_ADMIN]));
  const isTransporterAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.TRANSPORTER_ADMIN]));
  const isMaintenance = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.MAINTENANCE]));
  const isMaintenanceByCompany = useAppSelector(state =>
    isUserAuthorizedForMaintenanceByCompany(state.authentication.account.authorities, state.authentication.account.company)
  );
  const maintenanceModelList = useAppSelector(state => state.maintenanceModel.entities);

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getSortState(props.location, DEFAULT_ITEMS_PER_PAGE, 'name'), props.location.search)
  );
  const [nameSearch, setNameSearch] = useState<string>('');
  const [selectedMaintenanceModel, setSelectedMaintenanceModel] = useState<IMaintenanceModel | null>(null);

  const searchTypes: ISearchTypes[] = [
    {
      label: translate('fleetSenseApp.maintenanceModel.name'),
      placeholder: translate('fleetSenseApp.maintenanceModel.search'),
      value: nameSearch,
      setValue: setNameSearch,
    },
  ];

  const getAllEntities = useCallback(() => {
    dispatch(
      getMaintenanceModelSummary({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
        nameSearch,
      })
    );

    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (props.location.search !== endURL) {
      props.history.replace(`${props.location.pathname}${endURL}`);
    }
  }, [paginationState, updateSuccess]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const setActivePage = () => {
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
  };

  useEffect(() => {
    getAllEntities();
  }, [getAllEntities]);

  const { match } = props;

  return (
    <Fragment>
      <PageHeader
        headingId="maintenance-model-heading"
        headingDataCy="MaintenanceModelHeading"
        contentKey="fleetSenseApp.maintenanceModel.home.title"
        match={match}
        buttonTextKey="fleetSenseApp.maintenanceModel.home.createLabel"
        showButton={isAdmin || isManufacturerAdmin || isTransporterAdmin || isMaintenance || isMaintenanceByCompany}
      />

      <PageFilters setActivePage={setActivePage} isAdmin={isAdmin} searchTypes={searchTypes} />

      {maintenanceModelList?.length && !loading ? (
        <Table responsive>
          <thead>
            <tr>
              <th className="hand" onClick={sort('name')}>
                <Translate contentKey="fleetSenseApp.maintenanceModel.name">Name</Translate> <FontAwesomeIcon icon="sort" />
              </th>
              <th className="hand" onClick={sort('company')}>
                <Translate contentKey="fleetSenseApp.maintenanceModel.type">Type</Translate> <FontAwesomeIcon icon="sort" />
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {selectedMaintenanceModel && (
              <MaintenanceModelModal
                maintenanceModel={selectedMaintenanceModel}
                toggle={() => setSelectedMaintenanceModel(null)}
                isOpen={!!selectedMaintenanceModel}
              />
            )}
            {maintenanceModelList.map((maintenanceModel, i) => (
              <tr key={`entity-${i}`} data-cy="entityTable">
                <td onClick={() => setSelectedMaintenanceModel(maintenanceModel)} style={{ cursor: 'pointer' }}>
                  {maintenanceModel.name}
                </td>
                <td>{isAdminByCompanyType(maintenanceModel.company.type) ? 'Padrão' : '-'}</td>
                <td className="text-right">
                  <div className="btn-group flex-btn-group-container">
                    <Button
                      title={`Descrição: ${maintenanceModel.description}`}
                      onClick={() => setSelectedMaintenanceModel(maintenanceModel)}
                      color="info"
                      size="sm"
                      data-cy="entityDetailsButton"
                    >
                      <FontAwesomeIcon icon="eye" />{' '}
                    </Button>
                    {(!isAdminByCompanyType(maintenanceModel.company.type) || isAdmin) && (
                      <>
                        <Button
                          style={showElement(isAdmin || isManufacturerAdmin || isTransporterAdmin || isMaintenance || isMaintenanceByCompany)}
                          tag={Link}
                          to={`${match.url}/${maintenanceModel.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                          color="primary"
                          size="sm"
                          data-cy="entityEditButton"
                        >
                          <FontAwesomeIcon icon="pencil-alt" />{' '}
                          <span className="d-none d-md-inline">
                            <Translate contentKey="entity.action.edit">Edit</Translate>
                          </span>
                        </Button>
                        <Button
                          style={showElement(isAdmin || isManufacturerAdmin || isTransporterAdmin || isMaintenance || isMaintenanceByCompany)}
                          tag={Link}
                          to={`${match.url}/${maintenanceModel.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                          color="danger"
                          size="sm"
                          data-cy="entityDeleteButton"
                        >
                          <FontAwesomeIcon icon="trash" />{' '}
                          <span className="d-none d-md-inline">
                            <Translate contentKey="entity.action.delete">Delete</Translate>
                          </span>
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        loading && <Loading />
      )}
      {!loading && !maintenanceModelList.length && (
        <div className="alert alert-warning">
          <Translate contentKey="fleetSenseApp.maintenanceModel.home.notFound">No Maintenance Models found</Translate>
        </div>
      )}
      {totalItems && !loading && !!maintenanceModelInfoList.length ? (
        <div className="count-page">
          <Row>
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </Row>
          <Row>
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </Row>
        </div>
      ) : (
        <Fragment />
      )}
    </Fragment>
  );
};

export default MaintenanceModel;
